import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ApolloError, useApolloClient, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useRouteChanged } from '~/hooks/transition'
import { useMenuListQuery } from '~/hooks/server/menu'

import Service from '~/models/service'
import System from '~/models/system'
import Menu from '~/models/menu'
import { GetMenusMode, GetNavigationScroll } from '~/modules/menu/selectors'
import { menuListWriteQueryOptions } from '~/modules/menu/utils'
import { ViewMenus } from '~/modules/menu/actions'
import { HideLoading } from '~/modules/loading/actions'
import { SORT_MENUS } from '~/api/menu'
import { extractSortParam, SortResponse, SortVars } from '~/api/common'

import { isSameSortItems, sortModels, updateSort } from '~/common/utils/sort'
import { SpaceColor } from '~/common/define/color'
import { requestErrored } from '~/common/form/formError'
import { MenusMode, useMenuNewModalShowEvent } from '~/common/define/menu'
import { notEmptyModel } from '~/common/utils/model'
import { isMatchFuzzy } from '~/common/utils/search'
import { ServiceTourStep, useServiceTourPositionSync } from '~/common/define/tour'
import NavigationMenuSupport from '~/components/molecules/navigation/NavigationMenuSupport'
import NavigationMenuNew from '~/components/molecules/navigation/menu/NavigationMenuNew'
import NavigationMenuButton from '~/components/molecules/navigation/menu/NavigationMenuButton'
import SpaceNavigationToolMenu from '~/components/layout/navigation/SpaceNavigationToolMenu'
import Loading from '~/components/atoms/loading/Loading'
import Sortable, { SortableModel } from '~/components/utils/sort/Sortable'
import NavigationBorderFrame from '~/components/molecules/navigation/NavigationBorderFrame'

interface Props {
  color: SpaceColor
  service: Service
  system: System
  systemHeight: number
}

const SpaceNavigationMenus = ({color, service, system, systemHeight}: Props) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const client = useApolloClient()
  const menuNewRef = useServiceTourPositionSync(ServiceTourStep.MENU_NEW)
  const menuRef = useServiceTourPositionSync(ServiceTourStep.MENU_SHOW)
  const toolNewRef = useServiceTourPositionSync(ServiceTourStep.TOOL_NEW)

  const mode = useSelector(GetMenusMode)
  const scroll = useSelector(GetNavigationScroll)
  const [searchText, setSearchText] = useState<string>("")

  const {data, loading, refetch} = useMenuListQuery(notEmptyModel(system) ? system : service)

  const menus = useMemo(() => {
    const sortedMenus = data ? sortModels<Menu>(data.menus) : []
    if (sortedMenus && searchText) {
      return sortedMenus.map((menu: Menu) => {
        return {
          ...menu,
          childMenus: menu.childMenus.filter((childMenu) => {
            return isMatchFuzzy(searchText, childMenu.tool.name)
          })
        }
      }).filter((menu: Menu) => {
        return menu.childMenus.length > 0
      })
    }
    return sortedMenus
  }, [data, searchText])

  useRouteChanged(() => {
    refetch()
  })

  const [mutateSort] = useMutation<SortResponse, SortVars>(SORT_MENUS,
    {
      onCompleted() {
        dispatch(HideLoading())
      },
      onError(error: ApolloError) {
        requestErrored(error, dispatch, t)
      }
    }
  )

  const onListUpdated = useCallback((targetMenus: Menu[]) => {
    if (!isSameSortItems(targetMenus, menus)) {
      const updatedMenus = updateSort<Menu>(targetMenus)
      client.writeQuery(menuListWriteQueryOptions(service, system, updatedMenus))
      mutateSort({
        variables: {
          sort: extractSortParam(updatedMenus)
        }
      })
    }
  }, [menus, mutateSort])

  const callNewEvent = useMenuNewModalShowEvent()

  return (
    <>
      {loading && menus.length == 0 ?
        <Loading/> :
        <>
          <NavigationBorderFrame borderColor={color} bottom={notEmptyModel(system) || (systemHeight < scroll)} sticky>
            <NavigationMenuSupport
              onSearch={setSearchText}
              onAdd={callNewEvent}
              searchPlaceholder={t('navigation:tool.search')}
              newTooltipText={t('navigation:menu.new')}
              color={color}
              menuNewRef={menuNewRef}
            />
          </NavigationBorderFrame>
          <Sortable items={menus as SortableModel[]} onDragEnd={onListUpdated} disabled={mode != MenusMode.SORT}>
            {menus.map((menu: Menu, index) =>
              <SpaceNavigationToolMenu key={menu.id} menu={menu} color={color} service={service} system={system} searched={!!searchText}
                                       menuRef={!index ? menuRef : null} toolNewRef={!index ? toolNewRef : null}
              />
            )}
          </Sortable>
          {menus.length == 0 &&
            <NavigationMenuNew color={color} onClick={callNewEvent} text={t('navigation:menu.create')}/>
          }
        </>
      }
      {mode == MenusMode.SORT && <NavigationMenuButton text={t('navigation:complete')} onClick={() => dispatch(ViewMenus())}/>}
    </>
  )
}

export default SpaceNavigationMenus